/**
 * Feedback - 버그 리포트 & 개선 요구사항 모델
 *
 * 사용자가 버그 신고 또는 개선 요구사항을 등록하고,
 * 관리자/AI가 확인·답변할 수 있는 피드백 시스템.
 *
 * Firestore Path: feedbacks/{feedbackId}
 */

import { Uid } from './User';
import { msg } from '@lingui/macro';
import { MessageDescriptor } from '@lingui/core';

// ── 피드백 유형 ──
export type FeedbackType = 'bug' | 'feature';

// ── 피드백 상태 ──
export type FeedbackStatus = 'draft' | 'submitted' | 'reviewing' | 'resolving' | 'unresolved' | 'resolved';

// ── 우선순위 ──
export type FeedbackPriority = 'low' | 'medium' | 'high' | 'critical';

// ── Tier 1: 환경 정보 (제출 시점 스냅샷) ──
export interface FeedbackEnvironment {
  // 기기/브라우저
  platform: string;          // web | android | ios
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  viewportWidth: number;
  viewportHeight: number;
  devicePixelRatio: number;
  touchScreen: boolean;
  appVersion: string;
  /** 빌드 커밋 SHA (vite define 주입) — dev/prod 동일 코드 여부 식별자 */
  appCommit?: string;
  /** git describe (예: v1.4.0.4.1-rc.2) — QA 검증 라운드 식별자 */
  appDescribe?: string;
  otaBundleVersion: string | null;
  osInfo: string;
  browserInfo: string;
  language: string;
  currentUrl: string;
  // 앱 상태
  isLoggedIn: boolean;
  activeWorkspaceId: string | null;
  activeWorkspaceName: string | null;
  currentRoomId: string | null;
  workspaceCount: number;
  roomCount: number;
  isPWA: boolean;
  isNativeApp: boolean;
  // 성능/연결
  onlineStatus: boolean;
  connectionType: string | null;    // 4g, wifi, etc.
  effectiveDownlink: number | null;  // Mbps
  jsHeapUsedMB: number | null;      // Chrome only
  jsHeapLimitMB: number | null;
  pageLoadTimeMs: number | null;
}

// ── Tier 2: 링 버퍼 진단 데이터 (앱 부팅 시부터 수집) ──
export interface DiagnosticLogEntry {
  level: 'log' | 'warn' | 'error';
  message: string;
  timestamp: number;
}

export interface DiagnosticErrorEntry {
  message: string;
  source?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  timestamp: number;
}

export interface DiagnosticNetworkEntry {
  url: string;
  method: string;
  status: number;          // 0 = network failure
  statusText: string;
  timestamp: number;
}

export interface FeedbackDiagnostics {
  consoleLogs: DiagnosticLogEntry[];       // 최근 100건
  jsErrors: DiagnosticErrorEntry[];         // 최근 20건
  networkErrors: DiagnosticNetworkEntry[];  // 최근 20건
  collectedAt: number;                      // 스냅샷 시점
}

// ── 피드백 스레드 메시지 ──
export interface FeedbackMessage {
  id: string;
  content: string;
  authorId: string;
  authorName: string;
  authorRole: 'user' | 'admin' | 'ai';
  createdAt: Date;
}

// ── 스크린샷 엔트리 (캡처 기기별 메타데이터 포함) ──
export interface ScreenshotEntry {
  imageUrl: string;
  capturedAt: Date;
  platform: string;
  currentUrl: string;
  environment?: FeedbackEnvironment;
  diagnostics?: FeedbackDiagnostics;
}

// ── 피드백 본체 ──
export interface Feedback {
  id: string;
  type: FeedbackType;
  title: string;                     // AI 자동 생성
  description: string;
  tags: string[];                    // AI 자동 생성 태그
  screenshots: ScreenshotEntry[];    // 캡처 기기별 메타데이터 포
  environment: FeedbackEnvironment;
  diagnostics: FeedbackDiagnostics; // Tier 2 링 버퍼 스냅샷
  status: FeedbackStatus;
  priority: FeedbackPriority;

  // 사용자 정보
  userId: string;
  userName: string;
  userEmail: string;
  userPhotoUrl: string | null;
  workspaceId: string | null;
  workspaceName: string | null;

  // 관리
  assignedTo?: string;
  adminNote?: string;
  messages: FeedbackMessage[];

  // 감사
  createdAt: Date;
  updatedAt: Date;
}

// ── Draft 이미지 (캡처된 스크린샷, 피드백에 첨부 전 임시 보관) ──
export interface DraftImage {
  id: string;
  userId: string;
  imageUrl: string;
  capturedAt: Date;
  platform: string;       // web | android | ios
  currentUrl: string;     // 캡처 시점의 페이지 URL
  environment?: FeedbackEnvironment;    // 캡처 시점 환경 정보
  diagnostics?: FeedbackDiagnostics;    // 캡처 시점 진단 데이터
}

// ── 컬렉션 경로 ──
export const FEEDBACKS_COL = 'feedbacks';

/** Draft 이미지 서브컬렉션 경로: feedbacks/_draftImages/{userId} */
export function draftImagesPath(userId: string): string {
  return `${FEEDBACKS_COL}/_draftImages/${userId}`;
}

// ── 유형 레이블 ──
export const FEEDBACK_TYPE_LABELS: Record<FeedbackType, MessageDescriptor> = {
  bug: msg`Bug Report`,
  feature: msg`Feature Request`,
};

// ── 상태 레이블 ──
export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, MessageDescriptor> = {
  draft: msg`Draft`,
  submitted: msg`Submitted`,
  reviewing: msg`Reviewing`,
  resolving: msg`Resolving`,
  unresolved: msg`Unresolved`,
  resolved: msg`Resolved`,
};

// ── 우선순위 레이블 ──
export const FEEDBACK_PRIORITY_LABELS: Record<FeedbackPriority, MessageDescriptor> = {
  low: msg`Low`,
  medium: msg`Medium`,
  high: msg`High`,
  critical: msg`Critical`,
};

// ── 기본값 생성 ──
export function createDefaultFeedback(type: FeedbackType, userId: string, userName: string, userEmail: string, userPhotoUrl: string | null): Partial<Feedback> {
  return {
    id: Uid.get(),
    type,
    title: '',
    description: '',
    tags: [],
    screenshots: [],
    status: 'submitted',
    priority: 'medium',
    userId,
    userName,
    userEmail,
    userPhotoUrl,
    messages: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
}

// ── 빈 진단 데이터 ──
export function emptyDiagnostics(): FeedbackDiagnostics {
  return {
    consoleLogs: [],
    jsErrors: [],
    networkErrors: [],
    collectedAt: Date.now(),
  };
}
